/* eslint-disable react/no-danger */
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { format, isAfter, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';

import styles from '../../../styles/partials/live-game-card.module.scss';

const propTypes = {
    game: AppPropTypes.game,
    selected: PropTypes.bool,
    participants: PropTypes.number,
    small: PropTypes.bool,
    withDate: PropTypes.bool,
    withOrder: PropTypes.bool,
    withChoose: PropTypes.bool,
    titlePrefix: PropTypes.string,
    onClick: PropTypes.func,
    href: PropTypes.func,
    className: PropTypes.string,
    timeClassName: PropTypes.string,
};

const defaultProps = {
    game: null,
    selected: false,
    participants: null,
    small: false,
    withDate: false,
    withOrder: false,
    withChoose: false,
    titlePrefix: null,
    onClick: null,
    href: null,
    className: null,
    timeClassName: null,
};

function LiveGameCard({
    game,
    participants,
    selected,
    small,
    withDate,
    withOrder,
    withChoose,
    titlePrefix,
    onClick,
    href,
    className,
    timeClassName,
}) {
    const themeClassName = useThemeClassName(styles);

    const {
        id = null,
        title = null,
        subtitle = null,
        description = null,
        startAt = null,
        endAt = null,
        order = null,
    } = game || {};

    const onClickGame = useCallback(() => {
        if (onClick !== null) {
            onClick(game);
        }
    }, [onClick]);

    const { date, isEnded = false } = useMemo(() => {
        const start = startAt !== null ? parseISO(startAt) : null;
        const end = endAt !== null ? parseISO(endAt) : null;
        const parsedDate = start !== null ? format(start, 'yyyy-MM-dd') : null;
        const parsedTime = start !== null ? format(start, 'HH:mm') : null;
        return { date: parsedDate, time: parsedTime, isPast: isAfter(new Date(), end) };
    }, [startAt]);

    const finalTitle = useMemo(() => {
        if (title === null) {
            return null;
        }
        if (titlePrefix === null) {
            return title;
        }
        if (withOrder && order !== null) {
            return `${titlePrefix} ${order}: ${title}`;
        }
        return `${titlePrefix} ${title}`;
    }, [title, titlePrefix, withOrder]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.selected]: selected,
                    [styles.small]: small,
                    [styles.greyed]: isEnded,
                },
                themeClassName,
                className,
            ])}
            title={id}
        >
            <Button onClick={onClickGame} href={href} className={styles.button}>
                <div className={styles.left}>
                    {title !== null && title.length > 0 ? (
                        <h4 className={styles.title}>{finalTitle}</h4>
                    ) : null}
                    {description !== null && description.length > 0 ? (
                        <p className={styles.description}>{description}</p>
                    ) : null}
                    {participants !== null && participants > 0 ? (
                        <p className={styles.participants}>
                            <FormattedMessage
                                defaultMessage="Registered participants: {count}"
                                description="Card label"
                                values={{ count: participants }}
                            />
                        </p>
                    ) : null}
                </div>
                <div className={styles.right}>
                    {subtitle !== null ? (
                        <span
                            className={classNames([
                                styles.pill,
                                { [timeClassName]: timeClassName !== null },
                            ])}
                        >
                            {subtitle}
                        </span>
                    ) : null}
                    {withChoose && (onClick !== null || href !== null) ? (
                        <div className={styles.action}>
                            {!selected ? (
                                <div className={styles.label}>
                                    <FormattedMessage
                                        defaultMessage="Select"
                                        description="Button label"
                                    />
                                </div>
                            ) : (
                                <FontAwesomeIcon icon={faCircleCheck} className={styles.icon} />
                            )}
                        </div>
                    ) : null}
                    {withDate && date !== null ? (
                        <div className={styles.date}>
                            <span className={styles.day}>{date}</span>
                        </div>
                    ) : null}
                </div>
            </Button>
        </div>
    );
}

LiveGameCard.propTypes = propTypes;
LiveGameCard.defaultProps = defaultProps;

export default LiveGameCard;
